import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import ApiService from '../services/api';
import { AccountPathway } from '../constants/accountTypes';

export interface OrganizationContextData {
  organizationId: string;
  segment: AccountPathway;
  legalName: string;
  industry?: string;
  msmeProfileId?: string | null;
  enterpriseProfileId?: string | null;
  role: string;
}

interface OrganizationContextValue {
  organization: OrganizationContextData | null;
  isLoading: boolean;
  error: string | null;
  refreshOrganization: () => Promise<void>;
  clearOrganization: () => void;
}

const OrganizationContext = createContext<OrganizationContextValue | undefined>(undefined);

export const OrganizationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [organization, setOrganization] = useState<OrganizationContextData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshOrganization = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setOrganization(null);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await ApiService.getOrganizationContext();
      if (response?.success && response.data) {
        setOrganization(response.data as OrganizationContextData);
      } else {
        setOrganization(null);
        setError(response?.message || 'Organization context unavailable');
      }
    } catch (err) {
      setOrganization(null);
      setError(err instanceof Error ? err.message : 'Failed to load organization context');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearOrganization = useCallback(() => {
    setOrganization(null);
    setError(null);
  }, []);

  useEffect(() => {
    void refreshOrganization();

    const handleAuthChange = () => {
      void refreshOrganization();
    };

    window.addEventListener('carbon-auth-changed', handleAuthChange);
    return () => window.removeEventListener('carbon-auth-changed', handleAuthChange);
  }, [refreshOrganization]);

  const value = useMemo(
    () => ({
      organization,
      isLoading,
      error,
      refreshOrganization,
      clearOrganization,
    }),
    [organization, isLoading, error, refreshOrganization, clearOrganization]
  );

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = (): OrganizationContextValue => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};
